import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    InputAdornment,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { tenantAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Define the tenant type (same as in SuperAdminDashboard)
interface Tenant {
    id: string;
    name: string;
    subdomain: string;
    userCount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
}

const ClubsPage: React.FC = () => {
    const { user } = useAuth();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
    const [newTenantData, setNewTenantData] = useState({
        name: '',
        subdomain: '',
    });

    useEffect(() => {
        const fetchTenants = async () => {
            try {
                const response = await tenantAPI.getAllTenants();
                setTenants(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching tenants:', err);
                setError('Failed to load club data');
                setLoading(false);
            }
        };

        fetchTenants();
    }, []);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleOpenCreateDialog = () => {
        setOpenCreateDialog(true);
    };

    const handleCloseCreateDialog = () => {
        setOpenCreateDialog(false);
    };

    const handleNewTenantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewTenantData({
            ...newTenantData,
            [event.target.name]: event.target.value,
        });
    };

    const handleCreateTenant = async () => {
        try {
            await tenantAPI.createTenant(newTenantData);
            // Refresh tenant list
            const response = await tenantAPI.getAllTenants();
            setTenants(response.data);
            // Close dialog and reset form
            setOpenCreateDialog(false);
            setNewTenantData({ name: '', subdomain: '' });
        } catch (err) {
            console.error('Error creating tenant:', err);
            alert('Failed to create club. Please try again.');
        }
    };

    // Filter tenants based on search term
    const filteredTenants = tenants.filter(tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => window.location.reload()}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Container>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom component="div">
                    Clubs Management
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Search and Create Button */}
                <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <TextField
                            placeholder="Search clubs..."
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: '300px' }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreateDialog}
                        >
                            Create New Club
                        </Button>
                    </Paper>
                </Grid>

                {/* Overview Stats */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">{tenants.length}</Typography>
                        <Typography variant="body1">Total Clubs</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">
                            {tenants.reduce((total, tenant) => total + tenant.userCount, 0)}
                        </Typography>
                        <Typography variant="body1">Total Users</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                        <Typography variant="h4" color="primary">
                            {tenants.filter(tenant => tenant.status === 'active').length}
                        </Typography>
                        <Typography variant="body1">Active Clubs</Typography>
                    </Paper>
                </Grid>

                {/* Tenant List */}
                <Grid size={{ xs: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Subdomain</strong></TableCell>
                                    <TableCell align="right"><strong>Users</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Created</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTenants.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            No clubs found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredTenants.map((tenant) => (
                                        <TableRow key={tenant.id}>
                                            <TableCell>{tenant.name}</TableCell>
                                            <TableCell>{tenant.subdomain}</TableCell>
                                            <TableCell align="right">{tenant.userCount}</TableCell>
                                            <TableCell>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: 1,
                                                        bgcolor: tenant.status === 'active' ? 'success.light' : 'warning.light',
                                                        color: tenant.status === 'active' ? 'success.dark' : 'warning.dark',
                                                    }}
                                                >
                                                    {tenant.status.toUpperCase()}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => {
                                                        // Store tenant ID in local storage and redirect to admin dashboard
                                                        localStorage.setItem('tenantId', tenant.id);
                                                        window.location.href = '/dashboard';
                                                    }}
                                                >
                                                    Manage
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Create Club Dialog */}
            <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
                <DialogTitle>Create New Club</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Please enter the details for the new club.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="name"
                        label="Club Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newTenantData.name}
                        onChange={handleNewTenantChange}
                    />
                    <TextField
                        margin="dense"
                        name="subdomain"
                        label="Subdomain"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newTenantData.subdomain}
                        onChange={handleNewTenantChange}
                        helperText="This will be used for the club's URL: [subdomain].rallyon.com"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateDialog}>Cancel</Button>
                    <Button
                        onClick={handleCreateTenant}
                        variant="contained"
                        color="primary"
                        disabled={!newTenantData.name || !newTenantData.subdomain}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ClubsPage;
